import React, { useState } from 'react';
import Dropdown from '@components/common/Dropdown';
import Icon from '@components/Icon/Icon';
import { useLanguage } from '@contexts/LanguageContext';
import './Button.scss';

const Button = ({
  children,
  onClick,
  disabled = false,
  loading = false,
  variant = 'primary',
  size = 'medium',
  className = '',
  as: Component = 'button',
  // Multi-action props
  options = [],
  onOptionSelect,
  dropdownPosition = 'bottom-left',
  ...props
}) => {
  const { t } = useLanguage();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const hasOptions = options.length > 0;

  const buttonClasses = [
    'button',
    `button--${variant}`,
    `button--${size}`,
    loading && 'button--loading',
    className
  ].filter(Boolean).join(' ');

  const handleOptionClick = (option) => {
    if (onOptionSelect) {
      onOptionSelect(option);
    }
  };

  return (
    <div className="button-container">
      {hasOptions ? (
        // Multi-action button with dropdown - same base structure
        <Dropdown
          isOpen={isDropdownOpen}
          onToggle={setIsDropdownOpen}
          trigger={
            <div className="button-split">
              <Component
                className={`${buttonClasses} button-main`}
                onClick={onClick}
                disabled={disabled || loading}
                {...props}
              >
                {children}
              </Component>
              <button
                className={`${buttonClasses} button-dropdown`}
                disabled={disabled || loading}
                aria-label={t('common.moreOptions')}
                type="button"
              >
                <Icon
                  icon="chevron-down"
                  size="sm"
                  className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}
                />
              </button>
            </div>
          }
          closeOnItemClick={true}
          position={dropdownPosition}
        >
          <div className="button-options">
            {options.map((option) => (
              <button
                key={option.key}
                className="button-option"
                onClick={() => handleOptionClick(option)}
                disabled={disabled}
                type="button"
              >
                <span className="option-label">{option.label}</span>
                {option.description && (
                  <span className="option-description">{option.description}</span>
                )}
              </button>
            ))}
          </div>
        </Dropdown>
      ) : (
        // Simple button - same base structure
        <Component
          className={buttonClasses}
          onClick={onClick}
          disabled={disabled || loading}
          {...props}
        >
          {children}
        </Component>
      )}
    </div>
  );
};

export default Button;